import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {baseQuery} from '../base.api';

export const inspectionApi = createApi({
  reducerPath: 'inspection/api',
  baseQuery: baseQuery(import.meta.env.VITE_API_URL),
  keepUnusedDataFor: 0.0001,
  endpoints: build => ({
    getInspection: build.query({
      query: () => ({
        url: 'inspection-form/list',
      }),
      providesTags: ['Inspection'],
    }),
    getInspectionsPagination: build.query({
      query: params => ({
        url: `inspection-form?page=${params.page ? params.page : 1}`,
      }),
      providesTags: ['InspectionPagination'],
    }),
    getPeriods: build.query({
      query: () => ({
        url: 'inspection-form/periods',
      }),
    }),
    getLogSheetsPeriods: build.query({
      query: () => ({
        url: '/periods',
      }),
    }),
    getOrdersList: build.query({
      query: params => ({
        url: `orders/list?${params}`,
      }),
      invalidatesTags: ['OrderList'],
    }),
    getOrdersListFbo: build.query({
      query: params => ({
        url: `orders/list?${params?.id ? `agency_id=${params?.id}` : ''}${params?.page ? `&page=${params?.page}` : ''}`,
      }),
      invalidatesTags: ['OrderList'],
    }),
    getSubscriptionsFbo: build.query({
      query: params => ({
        url: `subscriptions/list?${params?.id ? `agency_id=${params?.id}` : ''}${params?.page ? `&page=${params?.page}` : ''}`,
      }),
      invalidatesTags: ['OrderList'],
    }),
    getOrder: build.query({
      query: id => ({
        url: `orders/${id}`,
      }),
      providesTags: ['CurrentOrder'],
    }),
    createInspection: build.mutation({
      query: body => ({
        url: 'inspection-form/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Inspection', 'InspectionPagination'],
    }),
    getFacilityByAgency: build.query({
      query: ({id, page}) => ({
        url: `facility?agency_id=${id}&page=${page}`,
      }),
      providesTags: ['Facility'],
    }),
    getFacility: build.query({
      query: () => ({
        url: 'facility/list',
      }),
      providesTags: ['Facility'],
    }),
    getFacilityIndex: build.query({
      query: data => ({
        url: `facility?page=${data?.page}&limit=10${data?.search ? '&search=' + `${data?.search}` : ''}`,
      }),
      providesTags: ['FacilityIndex'],
    }),
    createFacility: build.mutation({
      query: body => ({
        url: 'facility/create',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['Facility'],
    }),
    getFieldTypes: build.query({
      query: () => ({
        url: 'inspection-form/field-types',
      }),
      providerTags: ['FieldTypes'],
    }),
    createInspectionStep: build.mutation({
      query: body => ({
        url: 'inspection/step/create',
        method: 'POST',
        body,
      }),
    }),

    getEquipment: build.query({
      query: params => {
        const query = [
          `${params.page ? 'page=' + params.page : ''}`,
          params.equipment_type_id
            ? `equipment_type_id=${params.equipment_type_id}`
            : null,
          params.active ? `active=${params.active}` : null,
          params.facility_id ? `facility_id=${params.facility_id}` : null,
          params.search ? `search=${params.search.trim()}` : null,
          params.archived ? `archived=${params.archived}` : null,
          params.transfers ? `transfers=${params.transfers}` : null,
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `equipment?${query}`,
        };
      },
      providerTags: ['GetEquipment'],
    }),
    getEquipmentAll: build.query({
      query: params => ({
        url: `equipment`,
        params: {page: 1, per_page: 100, ...(params || {})},
      }),
      providerTags: ['GetEquipmentAll'],
    }),
    getAgenciesWithEquipments: build.query({
      query: params => ({
        url: `equipment/types/list-by-agency-with-equipments?form_code=${params}`,
      }),
      providerTags: ['GetAgenciesWithEquipment'],
    }),
    getCurrentEquipment: build.query({
      query: params => ({
        // TODO: ADD PAGINATION
        url: `equipment/${params}`,
      }),
      providerTags: ['CurrentEquipment'],
    }),
    equipmentDecommission: build.mutation({
      query: ({id, reason}) => ({
        url: `equipment/${id}/decommission`,
        method: 'POST',
        body: {reason},
      }),
      invalidatesTags: ['GetEquipment'],
    }),
    equipmentActivate: build.mutation({
      query: id => ({
        url: `equipment/${id}/activate`,
        method: 'POST',
      }),
    }),
    equipmentUnarchived: build.mutation({
      query: id => ({
        url: `equipment/${id}/unarchive`,
        method: 'POST',
      }),
    }),
    getReports: build.query({
      query: params => ({
        url: `equipment/tools-report${params.agency_id ? '?agency_id=' + params.agency_id : ''}`,
        method: 'GET',
      }),
    }),
    getReportHistory: build.query({
      query: params => ({
        url: `equipment/tools-history${params.equipment_id ? '?equipment_id=' + params.equipment_id : ''}`,
        method: 'GET',
      }),
    }),
    getInspectionById: build.query({
      query: params => ({
        url: `inspection-form/get/${params}`,
      }),
      providesTags: ['CurrentInspection'],
    }),
    getInspectionFacilityById: build.query({
      query: params => ({
        url: `inspection/get/${params}`,
      }),
      providesTags: ['CurrentInspection'],
    }),
    createCategory: build.mutation({
      query: body => ({
        url: 'inspection-form/category/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CurrentInspection'],
    }),
    deleteCategory: build.mutation({
      query: body => ({
        url: 'inspection-form/category/delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CurrentInspection'],
    }),
    changeCategory: build.mutation({
      query: body => ({
        url: 'inspection-form/category/rename',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CurrentInspection'],
    }),
    reorderCategory: build.mutation({
      query: body => ({
        url: 'inspection-form/category/reorder',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CurrentInspection'],
    }),
    updateField: build.mutation({
      query: body => ({
        url: 'inspection-form/category/update-fields',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CurrentInspection'],
    }),
    getEquipmentType: build.query({
      query: params => ({
        url: `equipment/types/list`,
        params,
      }),
      providesTags: ['EquipmentTypesList'],
    }),
    getEquipmentByType: build.query({
      query: () => ({
        url: `equipment/types/list/by-type`,
      }),
      providesTags: ['EquipmentTypesList'],
    }),

    getFacilityByType: build.query({
      query: () => ({
        url: `facility/simple-list`,
      }),
      providesTags: ['FacilitySimpleList'],
    }),
    createCustomEquipmentType: build.mutation({
      query: body => ({
        url: 'equipment/types/create-custom',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EquipmentTypesList'],
    }),
    createEquipmentType: build.mutation({
      query: body => ({
        url: 'equipment/types/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EquipmentTypesList'],
    }),
    getInspectionsByType: build.query({
      query: params => ({
        url: `inspection-form/list-by-type?type=${params}`,
      }),
      providesTags: ['InspectionsByType'],
    }),
    getCurrentInspection: build.query({
      query: params => ({
        url: `equipment/types/${params}`,
      }),
      providesTags: ['EquipmentTypesInspections'],
    }),
    updateEquipmentType: build.mutation({
      query: body => ({
        url: 'inspection-form/relating/assign',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'EquipmentTypesInspections',
        'GetCurrentFacilityInspections',
      ],
    }),
    completeInspection: build.mutation({
      query: body => ({
        url: 'inspection/save',
        method: 'POST',
        body,
      }),
    }),
    getInspectionByFacility: build.query({
      query: id => ({
        url: `inspection/list-web${id !== 0 ? `?facility_id=${id}` : ''}`,
        method: 'GET',
        // let s = "";
        // if (id !== 1) {
        //   for (let i = 0; i < id.length; i++) {
        //     s += `facility_id[]=${id[i]?.value}&`;
        //   }
        // } else {
        //   s = `facility_id[]=1`;
        // }
        // let s = typeof id === array ? id.forEach : id
        // return {

        // };
      }),
      providesTags: ['GetInspections'],
      invalidatesTags: [
        'GetCurrentFacilityInspections',
        'EquipmentTypesInspections',
      ],
    }),
    refuseInspections: build.mutation({
      query: body => ({
        url: 'inspection-form/relating/refuse',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'EquipmentTypesInspections',
        'GetCurrentFacilityInspections',
      ],
    }),
    getCorrectiveActions: build.query({
      query: params => ({
        url:
          `corrective-actions?closed=${params.closed}` +
          `${params.page ? '&page=' + params.page : '&page=1'}` +
          `${params.facility_id ? '&facility_id=' + params.facility_id : ''}` +
          `${
            params.equipment_id ? '&equipment_id=' + params.equipment_id : ''
          }` +
          `${params.date_from ? '&date_from=' + params.date_from : ''}` +
          `${params.date_to ? '&date_to=' + params.date_to : ''}` +
          `${params.search ? '&search=' + params.search : ''}`,
      }),
      providesTags: ['GetCorrectiveActions'],
    }),
    // Get single corrective action details by id for CorrectiveActionModal
    getCorrectiveAction: build.query({
      query: id => ({
        url: `corrective-actions/${id}`,
      }),
      providesTags: ['GetCorrectiveActions'],
    }),
    getCurrentFacilityInspections: build.query({
      query: params => ({
        url: `facility/${params}`,
      }),
      providesTags: ['GetCurrentFacilityInspections'],
    }),
    updateFacilityInfo: build.mutation({
      query: body => ({
        url: 'facility/update',
        method: 'POST',
        body,
      }),
    }),
    changeCorrectiveAction: build.mutation({
      query: body => ({
        url: 'corrective-actions/close',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['GetCorrectiveActions'],
    }),
    getReportList: build.query({
      query: params => ({
        url:
          `report/pdf/list?` +
          `${params.page ? '&page=' + params.page : '&page=1'}` +
          `${params.facility_id ? '&facility_id=' + params.facility_id : ''}` +
          `${params.type ? '&type=' + params.type : ''}` +
          `${params.date_from ? '&date_from=' + params.date_from : ''}` +
          `${params.date_to ? '&date_to=' + params.date_to : ''}` +
          `${params.search ? '&search=' + params.search : ''}`,
      }),
      providesTags: ['ReportList'],
    }),
    createReport: build.mutation({
      query: body => ({
        url: 'report/pdf/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ReportList'],
    }),
    createFuelReceiptReport: build.mutation({
      query: body => ({
        url: 'report/pdf/fuel/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ReportList'],
    }),
    removeReport: build.mutation({
      query: id => ({
        url: `report/pdf/delete/${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['ReportList'],
    }),
    checkReport: build.mutation({
      query: id => ({
        url: `report/pdf/check/${id}`,
        method: 'GET',
      }),
      invalidatesTags: ['ReportList'],
    }),
    reportPdfLink: build.mutation({
      query: id => ({
        url: `report/pdf/download/${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['ReportList'],
    }),
    equipmentsByFacility: build.query({
      query: facility_id => ({
        url: `equipment/types/list-by-facility-with-equipments?facility_id=${facility_id}`,
        method: 'GET',
      }),
    }),

    getStationsList: build.query({
      query: params => ({
        url: `stations/list`,
      }),
      providesTags: ['Stations'],
    }),
    createStation: build.mutation({
      query: body => ({
        url: 'stations/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Stations'],
    }),
    updateStation: build.mutation({
      query: body => ({
        url: 'stations/update',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Stations'],
    }),
    deleteStation: build.mutation({
      query: body => ({
        url: `stations/${body}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Stations'],
    }),
    getTerminalsList: build.query({
      query: params => ({
        url: `terminals/list`,
      }),
      providesTags: ['Terminals'],
    }),
    createTerminal: build.mutation({
      query: body => ({
        url: 'terminals/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Terminals'],
    }),
    updateTerminal: build.mutation({
      query: body => ({
        url: 'terminals/update',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Terminals'],
    }),
    deleteTerminal: build.mutation({
      query: body => ({
        url: `terminals/${body}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Terminals'],
    }),
    createEquipment: build.mutation({
      query: body => ({
        url: `equipment/create`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['GetEquipment'],
    }),
    updateEquipment: build.mutation({
      query: body => ({
        url: 'equipment/update',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['GetEquipment'],
    }),
    getUsers: build.query({
      query: params => ({
        url: `users/list${params ? `?agency_id=${params}` : ''}`,
      }),
      providesTags: ['UsersList'],
    }),
    getUsersIndex: build.query({
      query: params => ({
        url: `users/index?${params?.page ? `&page=${params?.page}` : ''}${params?.agency_id ? `&agency_id=${params?.agency_id}` : ''}${params?.search ? `&search=${params?.search}` : ''}${params?.filter ? `&filter=${params?.filter}` : ''}`,
      }),
      providesTags: ['UsersListIndex'],
    }),
    getUsersByAgency: build.query({
      query: params => ({
        url: `users/agency`,
      }),
      providesTags: ['UsersListByAgency'],
    }),
    getChosenUser: build.query({
      query: params => ({
        url: `users/${params}`,
      }),
      providesTags: ['ChosenUser'],
    }),
    getUsersRoles: build.query({
      query: params => ({
        url: `users/roles`,
      }),
      providesTags: ['UsersRolesList'],
    }),
    createUser: build.mutation({
      query: body => ({
        url: `users/create`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UsersList', 'CorporateDetails'],
    }),
    updateUser: build.mutation({
      query: body => ({
        url: `users/update`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UsersList', 'CorporateDetails'],
    }),
    updatePassword: build.mutation({
      query: ({id, password, password_confirmation}) => ({
        url: `users/update-password/${id}`,
        method: 'POST',
        body: {password, password_confirmation},
      }),
      invalidatesTags: ['UsersList', 'CorporateDetails'],
    }),
    deleteUser: build.mutation({
      query: body => ({
        url: `users/${body}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UsersList', 'CorporateDetails'],
    }),
    unAssignEquipment: build.mutation({
      query: body => ({
        url: `equipment/${body}/un-assign`,
        method: 'POST',
      }),
      invalidatesTags: ['GetEquipment'],
    }),
    unAssignFacility: build.mutation({
      query: body => ({
        url: `facility/${body}/un-assign`,
        method: 'POST',
      }),
      invalidatesTags: ['FacilityIndex'],
    }),
    assignFacility: build.mutation({
      query: body => ({
        url: `facility/${body}/assign`,
        method: 'POST',
      }),
      invalidatesTags: ['FacilityIndex'],
    }),
    getMissedInspections: build.query({
      query: params => ({
        url:
          `inspection/missed?page=${params.page ? params.page : 1}` +
          `&tab_name=${params?.tab_name}` +
          `${params.date_from ? '&date_from=' + params.date_from : ''}` +
          `${params.date_to ? '&date_to=' + params.date_to : ''}` +
          `${
            params.completed === 1
              ? '&completed=1'
              : params.completed === 0
                ? '&completed=0'
                : ''
          }` +
          `${params.facility_id ? '&facility_id=' + params.facility_id : ''}` +
          `${params.period ? '&period=' + params.period : ''}` +
          `${params.equipment_type_id ? '&equipment_type_id=' + params.equipment_type_id : ''}` +
          `${params.search ? '&search=' + params.search : ''}`,
      }),
      providesTags: ['MissedInspections'],
    }),
    getReturnToService: build.query({
      query: params => ({
        url:
          `inspection/return-to-service?page=${params.page ? params.page : 1}` +
          `${params.date_from ? '&date_from=' + params.date_from : ''}` +
          `${params.date_to ? '&date_to=' + params.date_to : ''}` +
          `${params.completed === 1 ? '&completed=1' : params.completed === 0 ? '&completed=0' : ''}` +
          `${params.facility_id ? '&facility_id=' + params.facility_id : ''}` +
          `${params.period ? '&period=' + params.period : ''}` +
          `${params.equipment_type_id ? '&equipment_type_id=' + params.equipment_type_id : ''}`,
      }),
      providesTags: ['ReturnToService'],
    }),
    updateMissedInspection: build.mutation({
      query: body => ({
        url: `inspection/missed/${body.id}/update`,
        method: 'POST',
        body: body.data,
      }),
      invalidatesTags: ['MissedInspections'],
    }),
    getServiceRequests: build.query({
      query: params => ({
        url: `service-requests?page=${params.page ? params.page : 1}${
          params.status ? `&status=${params.status}` : ''
        }`,
      }),
      providesTags: ['ServiceRequests'],
    }),
    updateServiceRequest: build.mutation({
      query: body => ({
        url: `service-requests/${body.id}/update`,
        method: 'POST',
        body: body.data,
      }),
      invalidatesTags: ['ServiceRequests'],
    }),
    getSheets: build.query({
      query: params => {
        const {
          page = '1',
          search = '',
          equipment_type_id,
          user_id,
          date_from,
          date_to,
          facility_id,
          status,
          period,
        } = params || {};

        const queryParams = new URLSearchParams({
          page,
          ...(search && {search}),
          ...(equipment_type_id && {equipment_type_id}),
          ...(user_id && {user_id}),
          ...(date_from && {date_from}),
          ...(date_to && {date_to}),
          ...(facility_id && {facility_id}),
          ...(status && {status}),
          ...(period && {period}),
        }).toString();

        return {url: `log-sheets?${queryParams}`};
      },
      providesTags: ['SheetsList'],
    }),
    removeLogSheet: build.mutation({
      query: body => ({
        url: `log-sheets/${body?.id}/delete`,
        method: 'POST',
      }),
      invalidatesTags: ['ServiceRequests'],
    }),
    getAgency: build.query({
      query: () => ({
        url: `agency/list`,
      }),
      providesTags: ['Agancy'],
    }),
    getTrialAgency: build.query({
      query: data => ({
        url: `agency/trials?limit=10&page=${data?.page}${data?.search ? `&search=${data?.search}` : ''}${data?.inspections_revision ? `&inspections_revision=${data?.inspections_revision}` : ''}${data?.corporate_id ? `&corporate_id=${data?.corporate_id}` : ''}${data?.inspection_enabled ? `&inspection_enabled=${data?.inspection_enabled}` : ''}${data?.custom_inspections_enabled ? `&custom_inspections_enabled=${data?.custom_inspections_enabled}` : ''}`,
      }),
      providesTags: ['AgencyTrials'],
    }),
    getAgencyIndex: build.query({
      query: data => ({
        url: `agency?page=${data?.page}${data?.search ? `&search=${data?.search}` : ''}`,
      }),
      providesTags: ['AgancyIndex'],
    }),
    getAgencySubscribed: build.query({
      query: data => ({
        url: `agency/subscribed?page=${data?.page}${data?.search ? `&search=${data?.search}` : ''}${data?.inspections_revision ? `&inspections_revision=${data?.inspections_revision}` : ''}${data?.corporate_id ? `&corporate_id=${data?.corporate_id}` : ''}${data?.inspection_enabled ? `&inspection_enabled=${data?.inspection_enabled}` : ''}${data?.custom_inspections_enabled ? `&custom_inspections_enabled=${data?.custom_inspections_enabled}` : ''}`,
      }),
      providesTags: ['AgancySubscribed'],
    }),
    getInvitedAgencyIndex: build.query({
      query: data => ({
        url: `agency/invited?page=${data?.page}${data?.search ? `&search=${data?.search}` : ''}${data?.inspections_revision ? `&inspections_revision=${data?.inspections_revision}` : ''}${data?.corporate_id ? `&corporate_id=${data?.corporate_id}` : ''}${data?.inspection_enabled ? `&inspection_enabled=${data?.inspection_enabled}` : ''}${data?.custom_inspections_enabled ? `&custom_inspections_enabled=${data?.custom_inspections_enabled}` : ''}`,
      }),
      providesTags: ['AgancyInvited'],
    }),
    deleteInvitedAgency: build.mutation({
      query: body => ({
        url: `users/${body}`,
        method: 'DELETE',
      }),
      providesTags: ['AgancyInvited'],
    }),
    resendInvitedAgency: build.mutation({
      query: body => ({
        url: `agency/resend-invitation/${body}`,
        method: 'POST',
      }),
      providesTags: ['AgancyInvited'],
    }),
    getTimezones: build.query({
      query: () => ({
        url: `agency/timezones`,
      }),
      providesTags: ['Timezones'],
    }),
    createAgency: build.mutation({
      query: body => ({
        url: `agency/create`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Agancy, AgancyIndex'],
    }),
    updateAgancy: build.mutation({
      query: ({id, data}) => {
        return {
          url: `agency/${id}/update`,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: ['Agancy, AgancyIndex'],
    }),
    startAgencyTrial: build.mutation({
      query: data => {
        return {
          url: `agency/start-inspection-trial`,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: ['AgencyTrials, AgancyIndex'],
    }),
    extendAgencyTrial: build.mutation({
      query: data => {
        return {
          url: `agency/update-inspection-trial`,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: ['AgencyTrials, AgancyIndex'],
    }),
    updateAgancySettings: build.mutation({
      query: body => {
        return {
          url: `account/update-agency-settings`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: ['Agancy, AgancyIndex'],
    }),
    updateAgancyProfile: build.mutation({
      query: body => {
        return {
          url: `account/update-agency-profile`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: ['Agancy, AgancyIndex'],
    }),
    updateAgancyProfileSettings: build.mutation({
      query: body => {
        return {
          url: `account/update-agency-profile-settings`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: ['Agancy, AgancyIndex'],
    }),
    updateAgencyCorrectiveSettings: build.mutation({
      query: body => {
        return {
          url: `account/update-agency-corrective-settings`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: ['Agancy, AgancyIndex'],
    }),
    refreshInspections: build.query({
      query: () => ({
        url: `inspection/create-periodic-inspections`,
      }),
      invalidatesTags: [`GetInspections`],
    }),
    getCurrentInspectionAndCategory: build.query({
      query: body => ({
        url: `inspection/get/${body.id}/${body.category_id}`,
      }),
      providesTags: ['CurrentInspectionAndCategory'],
    }),
    createService: build.mutation({
      query: body => ({
        url: `service-requests/create`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ServiceRequests'],
    }),
    saveEquipmentForm: build.mutation({
      query: body => ({
        url: `equipment/types/save-forms`,
        method: 'POST',
        body,
      }),
    }),
    getAssigned: build.query({
      query: params => ({
        url: `inspection-form/list-grouped-with-links`,
        params,
      }),
      invalidatesTags: [`GetAssigned`],
    }),
    deleteInspection: build.mutation({
      query: body => ({
        url: `inspection-form/delete`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['InspectionPagination'],
    }),
    saveProfilePhoto: build.mutation({
      query: body => ({
        url: `account/update-profile-photo`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ChosenUser'],
    }),
    uploadFile: build.mutation({
      query: body => ({
        url: `files/upload`,
        method: 'POST',
        body,
      }),
    }),
    forgotPassword: build.mutation({
      query: body => ({
        url: `auth/reset-password`,
        method: 'POST',
        body,
      }),
    }),
    sendCode: build.mutation({
      query: body => ({
        url: `auth/verify-reset-password`,
        method: 'POST',
        body,
      }),
    }),
    changePassword: build.mutation({
      query: body => ({
        url: `auth/set-password`,
        method: 'POST',
        body,
      }),
    }),
    inviteFbo: build.mutation({
      query: body => ({
        url: `auth/register-w-invite`,
        method: 'POST',
        body,
      }),
    }),
    getOutOfService: build.query({
      query: () => ({
        url: `equipment/list-decommissioned`,
      }),
      invalidatesTags: [`OutOfService`],
    }),
    getFuelTypes: build.query({
      query: () => ({
        url: `equipment/fuel-types`,
      }),
      invalidatesTags: [`OutOfService`],
    }),
    getSecretKey: build.mutation({
      query: () => ({
        url: `account/get-intent-secret`,
        method: 'POST',
      }),
    }),
    getCorporateSecretKey: build.mutation({
      query: () => ({
        url: `corporate/payment/get-intent-secret`,
        method: 'POST',
      }),
    }),
    updatePayment: build.mutation({
      query: body => ({
        url: `account/update-payment-method`,
        method: 'POST',
        body,
      }),
    }),
    updateCorporatePayment: build.mutation({
      query: body => ({
        url: `corporate/payment/update-payment-method`,
        method: 'POST',
        body,
      }),
    }),
    paymentCheckout: build.mutation({
      query: body => ({
        url: `payment/checkout`,
        method: 'POST',
        body,
      }),
    }),
    paymentCorporateCheckout: build.mutation({
      query: body => ({
        url: `corporate/payment/checkout`,
        method: 'POST',
        body,
      }),
    }),
    paymentConfirm: build.mutation({
      query: body => ({
        url: `payment/confirm`,
        method: 'POST',
        body,
      }),
    }),
    paymentCorporateConfirm: build.mutation({
      query: body => ({
        url: `corporate/payment/confirm`,
        method: 'POST',
        body,
      }),
    }),
    buyPhones: build.mutation({
      query: body => ({
        url: `payment/buy-phones`,
        method: 'POST',
        body,
      }),
    }),
    getHistorySubscription: build.query({
      query: () => ({
        url: `history/subscriptions`,
      }),
      invalidatesTags: [`HistorySubscription`],
    }),
    getHistoryPayments: build.query({
      query: () => ({
        url: `history/payments`,
      }),
      invalidatesTags: [`HistoryPayments`],
    }),
    getHistoryLeasing: build.query({
      query: () => ({
        url: `history/leasing`,
      }),
      invalidatesTags: [`HistoryLeasing`],
    }),
    getHistoryOrders: build.query({
      query: () => ({
        url: `history/orders`,
      }),
      invalidatesTags: [`HistoryOrders`],
    }),
    getPrices: build.query({
      query: () => ({
        url: `payment/prices`,
      }),
      invalidatesTags: [`PaymentPrices`],
    }),
    getCorporatePrices: build.query({
      query: () => ({
        url: `corporate/payment/prices`,
      }),
      invalidatesTags: [`PaymentPricesCorporate`],
    }),
    getFuelReceipt: build.query({
      query: params => ({
        url:
          `inspection/get-fuel-inspections-by-agency?agency_id=${params.agency}&is_complete=${params.isCompleted}` +
          `${params.page ? '&page=' + params.page : '&page=1'}` +
          `${params.facility_id ? '&facility_id=' + params.facility_id : ''}` +
          `${params.equipment_id ? '&equipment_id=' + params.equipment_id : ''}` +
          `${params.date_from ? '&date_from=' + params.date_from : ''}` +
          `${params.date_to ? '&date_to=' + params.date_to : ''}` +
          `${params.search ? '&search=' + params.search : ''}`,
      }),
      invalidatesTags: [`FuelReceiptList`],
    }),
    getSettings: build.query({
      query: () => ({
        url: `settings/index`,
      }),
      invalidatesTags: [`Settings`],
    }),
    getAgencyById: build.query({
      query: id => ({
        url: `agency/${id}/details`,
      }),
      invalidatesTags: [`AgencyById`],
    }),
    updateSetting: build.mutation({
      query: data => ({
        url: `settings/${data?.id}/update`,
        method: 'POST',
        body: data?.body,
      }),
      providesTags: ['Settings'],
    }),
    updateFboPrices: build.mutation({
      query: data => ({
        url: `agency/${data?.id}/update-prices`,
        method: 'POST',
        body: data?.body,
      }),
      providesTags: ['AgancyIndex'],
    }),
    updateBilling: build.mutation({
      query: body => ({
        url: `account/update-address`,
        method: 'POST',
        body: body,
      }),
    }),
    updateCorporateBilling: build.mutation({
      query: body => ({
        url: `corporate/payment/update-address`,
        method: 'POST',
        body: body,
      }),
    }),
    //
    updateOrder: build.mutation({
      query: data => ({
        url: `orders/${data?.id}`,
        method: 'POST',
        body: data?.body,
      }),
      providesTags: ['CurrentOrder'],
    }),
    createManualOrder: build.mutation({
      query: body => ({
        url: `orders/create-manual-order`,
        method: 'POST',
        body: body,
      }),
      providesTags: ['OrderList'],
    }),
    updateOrderShippingAddress: build.mutation({
      query: data => ({
        url: `orders/${data.id}/update-address`,
        method: 'POST',
        body: data.body,
      }),
    }),
    deleteEquipment: build.mutation({
      query: id => ({
        url: `equipment/${id}/delete`,
        method: 'POST',
      }),
      providesTags: ['EquipmentList'],
    }),
    permanentlyDelete: build.mutation({
      query: id => ({
        url: `/equipment/${id}/permanently-delete`,
        method: 'POST',
      }),
      providesTags: ['EquipmentList'],
    }),
    updateUserProfile: build.mutation({
      query: body => ({
        url: `account/update-profile`,
        method: 'POST',
        body: body,
      }),
    }),
    updateUserProfilePassword: build.mutation({
      query: body => ({
        url: `account/update-password`,
        method: 'POST',
        body: body,
      }),
    }),
    cancelSubscription: build.mutation({
      query: body => ({
        url: `agency/${body?.id}/cancel-subscription`,
        method: 'POST',
        body: body.data,
      }),
      providesTags: ['AgancyIndex, AgencyById'],
    }),
    getUsersByAgencyId: build.query({
      query: id => ({
        url: `agency/${id}/users`,
      }),
      invalidatesTags: [`UsersByAgency`],
    }),
    clearInspections: build.mutation({
      query: params => ({
        url: `log-sheets/delete?page=${params?.page ? params?.page : '1'}${
          params?.search && params?.search !== ''
            ? `&search=${params?.search}`
            : ''
        }${
          params?.equipment_type_id
            ? `&equipment_type_id=${params?.equipment_type_id}`
            : ''
        }${params?.user_id ? `&user_id=${params?.user_id}` : ''}${
          params?.date_from ? `&date_from=${params?.date_from}` : ''
        }${params?.date_to ? `&date_to=${params?.date_to}` : ''}
        ${params?.facility_id ? `&facility_id=${params?.facility_id}` : ''}
        ${params?.status ? `&status=${params?.status}` : ''}
        ${params?.period ? `&period=${params?.period}` : ''}`,
        method: 'POST',
      }),
      invalidatesTags: ['SheetsList'],
    }),
    clearInspectionsFbo: build.mutation({
      query: body => ({
        url: `agency/clear-inspections`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    reactivateFboEquipments: build.mutation({
      query: body => ({
        url: `agency/reactivate-equipments`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    createFiltersChange: build.mutation({
      query: body => ({
        url: `inspection/create/period/filter-change`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    createFuelReceiptInspection: build.mutation({
      query: body => ({
        url: `inspection/create/period/fuel-receipt`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    createFuelQualityRecord: build.mutation({
      query: body => ({
        url: `inspection/create/period/fuel-quality`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    createDemandPeriod: build.mutation({
      query: body => ({
        url: `inspection/create/period/on-demand`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    getCustomerRequests: build.query({
      query: params => ({
        url: `customer-requests/list?page=${params.page}`,
      }),
      invalidatesTags: ['CustomerRequestsRow'],
    }),
    getFieldOptions: build.query({
      query: url => ({
        url,
      }),
    }),
    getSignatureBases: build.query({
      query: () => ({
        url: 'signature-api/bases',
      }),
    }),
    updateCustomerRequest: build.mutation({
      query: body => ({
        url: `customer-requests/update-status`,
        method: 'POST',
        body: body,
      }),
      providesTags: ['CustomerRequestsRow'],
    }),
    sentCustomerLink: build.mutation({
      query: body => ({
        url: `customer-requests/send-sing-up-link`,
        method: 'POST',
        body: body,
      }),
      providesTags: ['CustomerRequestsRow'],
    }),
    dashboardPersonalStats: build.query({
      query: params => ({
        url: `dashboard/personnel-stats${params?.agency ? `?agency_id=${params?.agency}` : ''}`,
        ...(params?.agency != null && {
          headers: {agency: String(params.agency)},
        }),
      }),
      invalidatesTags: ['DashboardPersonalStats'],
    }),
    dashboardEquipmentStats: build.query({
      query: params => {
        return {
          url: `dashboard/equipment-stats`,
          ...(params?.agency != null && {
            headers: {agency: String(params.agency)},
          }),
        };
      },
      invalidatesTags: ['DashboardEquipmentStats'],
    }),
    dashboardPersonalInfo: build.query({
      query: params => {
        const search = new URLSearchParams();
        if (params?.name) search.set('filter_type', params.name);
        if (params?.agency != null) search.set('agency_id', params.agency);
        const query = search.toString();
        return {
          url: `dashboard/personal-info${query ? `?${query}` : ''}`,
          ...(params?.agency != null && {
            headers: {agency: String(params.agency)},
          }),
        };
      },
      invalidatesTags: ['DashboardPersonalInfo'],
    }),
    dashboardOutStandingInspections: build.query({
      query: params => ({
        url: `dashboard/outstanding-inspections${params?.agency != null ? `?agency_id=${params.agency}` : ''}`,
        ...(params?.agency != null && {
          headers: {agency: String(params.agency)},
        }),
      }),
      invalidatesTags: ['DashboardOutStandingInspections'],
    }),
    dashboardOutStandingRts: build.query({
      query: params => ({
        url: `dashboard/outstanding-rts${params?.agency != null ? `?agency_id=${params.agency}` : ''}`,
        ...(params?.agency != null && {
          headers: {agency: String(params.agency)},
        }),
      }),
      invalidatesTags: ['DashboardOutStandingRts'],
    }),
    dashboardCorrectiveActions: build.query({
      query: params => ({
        url: `dashboard/corrective-actions${params?.agency != null ? `?agency_id=${params.agency}` : ''}`,
        ...(params?.agency != null && {
          headers: {agency: String(params.agency)},
        }),
      }),
      invalidatesTags: ['DashboardCorrectiveActions'],
    }),
    dashboardOpenStats: build.query({
      query: params => ({
        url: `dashboard/open-stats${params?.agency != null ? `?agency_id=${params.agency}` : ''}`,
        ...(params?.agency != null && {
          headers: {agency: String(params.agency)},
        }),
      }),
      invalidatesTags: ['DashboardOpenStats'],
    }),
    dashboardAveragePerInspection: build.query({
      // query: ({filter_period, facility_id, equipment_type_id, employee_id}) => ({
      //   url: `dashboard/average-time?filter_period=${filter_period}
      //         ${facility_id ? "&facility_id=" + facility_id : ""}
      //         ${equipment_type_id ? "&equipment_type_id=" + equipment_type_id : ""}
      //         ${employee_id ? "&employee_id=" + employee_id : ""}`,
      // }),
      query: params => ({
        url: `dashboard/average-time${params ? `?${params}` : ''}`,
      }),
      invalidatesTags: ['DashboardAveragePerInspection'],
    }),
    dashboardInspections: build.query({
      query: ({period, agency}) => {
        const url = `dashboard/inspections?filter_period=${period}${agency != null ? `&agency_id=${agency}` : ''}`;
        return {
          url,
          ...(agency != null && {
            headers: {agency: String(agency)},
          }),
        };
      },
      invalidatesTags: ['dashboardInspections'],
    }),
    canAddFacility: build.mutation({
      query: body => ({
        url: `agency/update-ability-to-add-facility`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    metricsInspectionsPlanned: build.query({
      query: ({agency}) => ({
        url: `dashboard/inspections-last-week/?agency_id=${agency}`,
      }),
      invalidatesTags: ['metricsPlanned'],
    }),
    metricsInspectionsYesterday: build.query({
      query: ({agency}) => ({
        url: `dashboard/inspections-yesterday?agency_id=${agency}`,
      }),
      invalidatesTags: ['metricsYesterday'],
    }),
    metricsInspectionsMissed: build.query({
      query: ({agency, period, filter_for_period, limit}) => ({
        url: `dashboard/inspections-missed-last-month?agency_id=${agency}
            ${period ? `&period=${period}` : ''}
            ${filter_for_period ? `&filter_for_period=${filter_for_period}` : ''}
            ${limit ? `&limit=${limit}` : ''}
          `,
        ...(agency != null && {
          headers: {agency: String(agency)},
        }),
      }),
      invalidatesTags: ['metricsMissed'],
    }),
    metricsEquipmentCountType: build.query({
      query: ({agency, equipment_type_id}) => ({
        url: `dashboard/equipment-count-by-type?agency_id=${agency}
           ${equipment_type_id ? `&equipment_type_id=${equipment_type_id}` : ''}
          `,
      }),
      invalidatesTags: ['metricsEquipmentCount'],
    }),
    metricsEquipmentRemovedYesterday: build.query({
      query: ({agency}) => ({
        url: `dashboard/equipment-removed-yesterday?agency_id=${agency}`,
      }),
      invalidatesTags: ['metricsEquipmentRemoved'],
    }),
    metricsUpcomingInspections: build.query({
      query: ({agency, equipment_id, facility_id}) => ({
        url: `dashboard/upcoming-inspections?agency_id=${agency}${equipment_id ? `&equipment_id=${equipment_id}` : ''}
          ${facility_id ? `&facility_id=${facility_id}` : ''}
        `,
        ...(agency != null && {
          headers: {agency: String(agency)},
        }),
      }),
      invalidatesTags: ['metricsUpcoming'],
    }),
    metricsCorrectiveActionsStats: build.query({
      query: ({agency}) => ({
        url: `dashboard/corrective-action-stats?agency_id=${agency}`,
        ...(agency != null && {
          headers: {agency: String(agency)},
        }),
      }),
      invalidatesTags: ['metricsCorrectiveActionsStatistics'],
    }),
    metricsInspectionByEquipmentType: build.query({
      query: ({agency, equipment_type_id}) => ({
        url: `dashboard/inspection-metrics-by-equipment-type?agency_id=${agency}
            ${equipment_type_id ? `&equipment_id=${equipment_type_id}` : ''}
          `,
      }),
      invalidatesTags: [''],
    }),
    metricsAverageInspectionDuration: build.query({
      query: ({agency, equipment_type_id}) => ({
        url: `dashboard/average-inspection-duration?agency_id=${agency}
          ${equipment_type_id ? `&equipment_type_id=${equipment_type_id}` : ''}`,
      }),
      invalidatesTags: [''],
    }),
    metricsEquipmentDecommissions: build.query({
      query: ({agency, period}) => ({
        url: `dashboard/equipment-decommissions?agency_id=${agency}&period=${period}`,
      }),
      invalidatesTags: [''],
    }),
    metricsEquipmentRemoved: build.query({
      query: ({agency}) => ({
        url: `dashboard/equipment-decommission-stats?agency_id=${agency}`,
      }),
      invalidatesTags: [''],
    }),
    corporateList: build.query({
      query: params => ({
        url: `corporate/list?status=${params.status}`,
      }),
      invalidatesTags: [''],
    }),
    createPeriodInspection: build.mutation({
      query: ({period, facility_id, code}) => ({
        url: `inspection/create/period/${period}`,
        method: 'POST',
        body: {
          facility_id: facility_id,
          form_code: code,
        },
      }),
      providesTags: [''],
    }),
    createCorporateAccount: build.mutation({
      query: body => ({
        url: `auth/corporate-registration`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    assignFboWithCorporate: build.mutation({
      query: body => ({
        url: `corporate/assign-agency`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    unAssignFbo: build.mutation({
      query: body => ({
        url: `corporate/un-assign-agency`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    regionsList: build.query({
      query: data => ({
        url: `region/list${data?.corporate_id ? `?corporate_id=${data?.corporate_id}` : ''}`,
      }),
      invalidatesTags: [''],
    }),
    regionDetails: build.query({
      query: id => ({
        url: `region/${id}/details`,
      }),
      invalidatesTags: [''],
    }),
    createRegion: build.mutation({
      query: body => ({
        url: `region/create-update-assign`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    deleteRegion: build.mutation({
      query: id => ({
        url: `region/${id}/delete`,
        method: 'POST',
      }),
      providesTags: [''],
    }),
    dashboardCorporateFBO: build.query({
      query: arg => {
        const isString = typeof arg === 'string';
        const filter = isString ? arg : arg?.filter ?? 'worst_to_best';
        const pageRaw = isString ? 1 : arg?.page ?? 1;
        const page = Math.max(1, parseInt(pageRaw, 10) || 1);
        const perPageRaw = isString ? undefined : arg?.per_page;
        let perPage;
        if (perPageRaw != null) {
          const n = parseInt(perPageRaw, 10);
          if (!Number.isNaN(n)) {
            perPage = Math.min(100, Math.max(1, n));
          }
        }
        const searchRaw = isString ? '' : arg?.search;
        const search =
          typeof searchRaw === 'string' && searchRaw.trim().length >= 3
            ? searchRaw.trim()
            : undefined;

        const params = new URLSearchParams();
        params.set('filter', filter);
        params.set('page', String(page));
        if (perPage != null) {
          params.set('per_page', String(perPage));
        }
        if (search) {
          params.set('search', search);
        }
        return {
          url: `corporate/dashboard/agency-metrics?${params.toString()}`,
        };
      },
      invalidatesTags: [''],
    }),
    dashboardCorporateEquipment: build.query({
      query: () => ({
        url: `corporate/dashboard/equipments`,
      }),
      invalidatesTags: [''],
    }),
    dashboardCorporatePersonnel: build.query({
      query: () => ({
        url: `corporate/dashboard/personnel`,
      }),
      invalidatesTags: [''],
    }),
    updateCorporateProfile: build.mutation({
      query: body => ({
        url: `corporate/profile/update-profile-settings`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    updateCorporateCorrective: build.mutation({
      query: body => ({
        url: `corporate/profile/update-corrective-settings`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    corporateDetails: build.query({
      query: id => ({
        url: `corporate/${id}/details`,
      }),
      invalidatesTags: ['CorporateDetails'],
    }),
    updateCorporate: build.mutation({
      query: ({body, id}) => ({
        url: `corporate/${id}/update`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    corporateUsersList: build.query({
      query: id => ({
        url: `corporate/users/list`,
      }),
      invalidatesTags: ['CorporateUsers'],
    }),
    agencyWaterTestKitTypes: build.query({
      query: id => ({
        url: `agency/water-test-kit-types`,
      }),
      invalidatesTags: ['CorporateUsers'],
    }),
    updateCorporatePricing: build.mutation({
      query: ({body, id}) => ({
        url: `corporate/update-prices/${id}`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    updateSettingsAsCorporate: build.mutation({
      query: ({body, id}) => ({
        url: `agency/${id}/update-settings-as-corporate`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    deleteAgency: build.mutation({
      query: id => ({
        url: `agency/${id}/delete`,
        method: 'POST',
      }),
      providesTags: [''],
    }),
    skipInspection: build.mutation({
      query: inspection_id => ({
        url: `inspection/skip?inspection_id=${inspection_id}`,
        method: 'POST',
      }),
      providesTags: [''],
    }),
    getTools: build.query({
      query: params => ({
        url: `equipment/tools?page=${params.page}${params.search.length > 0 ? `&search=${params.search}` : ''}${params.facility_id ? `&facility_id=${params.facility_id}` : ''}${params.archived ? `&archived=${params.archived}` : ''}${typeof params.active === 'boolean' ? `&active=${params.active}` : ''}`,
      }),
      invalidatesTags: ['ToolsList'],
    }),
    getCalibrations: build.query({
      query: params => ({
        url: `inspection/get-calibration-inspections-by-agency?agency_id=${params?.agency_id}
        ${params?.inspection_type ? `&inspection_type=${params?.inspection_type}` : ''}
        ${params?.search?.length > 0 ? `&search=${params?.search}` : ''}
        ${params.is_complete === 'completed' ? `&is_complete=${1}` : params.is_complete === 'new' ? `&is_complete=${0}` : ''}
        ${params.date_from && params.date_from !== 'Invalid date' ? `&date_from=${params.date_from}` : ''}
        ${params.date_to && params.date_to !== 'Invalid date' ? `&date_to=${params.date_to}` : ''}
        ${params.equipment_id ? `&equipment_id=${params.equipment_id}` : ''}
        `,
      }),
      invalidatesTags: ['CalibrationsList'],
    }),
    getComments: build.query({
      query: inspection_id => ({
        url: `inspection/get-comments?inspection_id=${inspection_id}`,
      }),
      invalidatesTags: ['CommentsList'],
    }),
    addComments: build.mutation({
      query: body => ({
        url: `inspection/add-comment`,
        method: 'POST',
        body,
      }),
      providesTags: ['CommentsList'],
    }),
    removeFacility: build.mutation({
      query: id => ({
        url: `facility/${id}/delete`,
        method: 'POST',
      }),
      providesTags: ['FacilityList'],
    }),
    agencyInspectionEnable: build.mutation({
      query: body => ({
        url: `agency/enable-inspection`,
        method: 'POST',
        body,
      }),
      providesTags: [''],
    }),
    agencyDeactivate: build.mutation({
      query: id => ({
        url: `agency/${id}/deactivate`,
        method: 'POST',
      }),
      providesTags: [''],
    }),
    agencyReactivate: build.mutation({
      query: id => ({
        url: `agency/${id}/reactivate`,
        method: 'POST',
      }),
      providesTags: [''],
    }),
    agencySetSubscription: build.mutation({
      query: body => ({
        url: `agency/assign-subscription`,
        method: 'POST',
        body,
      }),
      providesTags: [''],
    }),
    agencyInactive: build.query({
      query: params => ({
        url: `agency/inactive?page=${params?.page}&limit=10${params?.search ? `&search=${params?.search}` : ''}${params?.inspections_revision ? `&inspections_revision=${params?.inspections_revision}` : ''}${params?.corporate_id ? `&corporate_id=${params?.corporate_id}` : ''}${params?.inspection_enabled ? `&inspection_enabled=${params?.inspection_enabled}` : ''}${params?.custom_inspections_enabled ? `&custom_inspections_enabled=${params?.custom_inspections_enabled}` : ''}`,
      }),
      invalidatesTags: ['InactiveList'],
    }),
    corporateUpdateStatus: build.mutation({
      query: data => ({
        url: `corporate/${data?.id}/update-status`,
        method: 'POST',
        body: data?.body,
      }),
      providesTags: [''],
    }),
    setAgencyToUser: build.mutation({
      query: body => ({
        url: `account/set-agency`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    demandInspections: build.query({
      query: params => ({
        url: `agency/on-demand-inspections`,
      }),
      invalidatesTags: ['demandInspections'],
    }),
    createHoseEquipment: build.mutation({
      query: body => ({
        url: `equipment/hose/create`,
        method: 'POST',
        body: body,
      }),
      providesTags: [''],
    }),
    updateHoseEquipment: build.mutation({
      query: data => ({
        url: `equipment/hose/${data?.id}/update`,
        method: 'POST',
        body: data?.body,
      }),
      providesTags: [''],
    }),
    deleteHoseEquipment: build.mutation({
      query: data => ({
        url: `equipment/hose/${data?.id}/delete`,
        method: 'POST',
      }),
      providesTags: [''],
    }),

    getCustomCategories: build.query({
      query: params => ({
        url: `custom-inspection/categories?page=${params.page}${params.is_corporate ? `&corporate=${params.is_corporate}` : ''}${params?.search?.length > 0 ? `&search=${params.search}` : ''}`,
      }),
      invalidatesTags: ['CustomInspectionsList'],
    }),
    getCustomFormsInspections: build.query({
      query: params => ({
        url: `custom-inspection/forms?page=${params.page}${params?.search?.length > 0 ? `&search=${params.search}` : ''}${params?.corporate ? `&corporate=true` : ''}`,
      }),
      invalidatesTags: ['CustomCategoriesList'],
    }),
    getCustomFormDetails: build.query({
      query: id => ({
        url: `custom-inspection/categories/${id}`,
      }),
      invalidatesTags: ['CustomCategoryDetails'],
    }),
    createCustomCategory: build.mutation({
      query: body => ({
        url: `custom-inspection/categories/create`,
        method: 'POST',
        body,
      }),
      providesTags: ['CustomCategoriesList'],
    }),
    updateCustomCategory: build.mutation({
      query: data => ({
        url: `custom-inspection/categories/${data.id}/update`,
        method: 'POST',
        body: data.body,
      }),
      providesTags: ['CustomCategoriesList'],
    }),
    createCustomInspection: build.mutation({
      query: body => ({
        url: `custom-inspection/forms/create`,
        method: 'POST',
        body,
      }),
      providesTags: ['CustomCategoriesList'],
    }),
    updateCustomInspection: build.mutation({
      query: data => ({
        url: `custom-inspection/forms/${data?.id}/update`,
        method: 'POST',
        body: data.body,
      }),
      providesTags: ['CustomCategoriesList'],
    }),
    getCustomInspectionsDetails: build.query({
      query: id => ({
        url: `custom-inspection/forms/${id}`,
      }),
      invalidatesTags: ['CustomInspectionsDetails'],
    }),
    publishCustomInspection: build.mutation({
      query: id => ({
        url: `custom-inspection/forms/publish/${id}`,
        method: 'POST',
      }),
      providesTags: ['CustomCategoriesList'],
    }),
    extendCustomInspection: build.mutation({
      query: data => ({
        url: `custom-inspection/forms/extend/${data.id}`,
        method: 'POST',
        body: data.body,
      }),
      providesTags: ['CustomInspections'],
    }),
    updateOpenDays: build.mutation({
      query: data => ({
        url: `custom-inspection/forms/${data.id}/update-open-days`,
        method: 'POST',
        body: {open_days: data.open_days},
      }),
      providesTags: ['CustomInspections'],
    }),
    assignCustomInspection: build.mutation({
      query: data => ({
        url: `custom-inspection/forms/accept/${data.id}`,
        method: 'POST',
        body: data.body,
      }),
      providesTags: ['CustomInspections'],
    }),
    getCustomFormDetailsAgencies: build.query({
      query: id => ({
        url: `custom-inspection/forms/agencies/${id}`,
      }),
      invalidatesTags: ['CustomCategoryDetails'],
    }),
    getCustomFormDetailsAgenciesEquipments: build.query({
      query: data => ({
        url: `custom-inspection/forms/${data?.form_id}/agency/${data?.agency_id}/applied-items`,
      }),
      invalidatesTags: ['CustomCategoryDetails'],
    }),
    deleteCustomInspectionCategory: build.mutation({
      query: id => ({
        url: `custom-inspection/categories/delete/${id}`,
        method: 'POST',
      }),
      providesTags: ['CustomInspections'],
    }),
    deleteCustomInspection: build.mutation({
      query: id => ({
        url: `custom-inspection/forms/delete/${id}`,
        method: 'POST',
      }),
      providesTags: ['Categories'],
    }),
    toggleCustomForm: build.mutation({
      query: id => ({
        url: `agency/${id}/toggle-custom-inspections`,
        method: 'POST',
      }),
      providesTags: ['Agencies'],
    }),
    toggleCorporateCustomForm: build.mutation({
      query: id => ({
        url: `corporate/${id}/toggle-custom-inspections`,
        method: 'POST',
      }),
      providesTags: ['CorporateList'],
    }),
    removeFuelInspection: build.mutation({
      query: id => ({
        url: `inspection/${id}/delete-fuel-inspection`,
        method: 'POST',
      }),
      providesTags: [''],
    }),
    setManualDate: build.mutation({
      query: data => {
        return {
          url: `inspection/${data?.id}/set-manual-date`,
          method: 'POST',
          body: data?.body,
        };
      },
      providesTags: [''],
    }),
    updateUserRole: build.mutation({
      query: ({id, body}) => ({
        url: `users/${id}/update-role/`,
        method: 'POST',
        body: body,
      }),
    }),
    updateUserSsoIgnore: build.mutation({
      query: ({id, body}) => ({
        url: `users/${id}/set-role-ignore/`,
        method: 'POST',
        body: body,
      }),
    }),
    SetUserRegion: build.mutation({
      query: ({id, body}) => ({
        url: `users/${id}/set-region/`,
        method: 'POST',
        body: body,
      }),
    }),
    loginAsUser: build.mutation({
      query: userId => {
        const formData = new FormData();
        formData.append('user_id', userId);
        return {
          url: `account/login-as-user`,
          method: 'POST',
          body: formData,
        };
      },
    }),
    getCustomInspectionsPeriods: build.query({
      query: () => ({
        url: `custom-inspection/periods`,
      }),
      invalidatesTags: ['CustomInspectionsPeriods'],
    }),
    transferEquipment: build.mutation({
      query: ({id, body}) => ({
        url: `equipment/${id}/transfer`,
        method: 'POST',
        body: body,
      }),
    }),
  }),
});
export const {
  useGetInspectionQuery,
  useGetFacilityQuery,
  useLazyGetFacilityQuery,
  useLazyGetFacilityIndexQuery,
  useCreateFacilityMutation,
  useGetPeriodsQuery,
  useGetLogSheetsPeriodsQuery,
  useCreateInspectionMutation,
  useGetFieldTypesQuery,
  useCreateInspectionStepMutation,
  useLazyGetEquipmentQuery,
  useLazyGetEquipmentAllQuery,
  useCreateCategoryMutation,
  useLazyGetInspectionByIdQuery,
  useDeleteCategoryMutation,
  useChangeCategoryMutation,
  useUpdateFieldMutation,
  useReorderCategoryMutation,
  useGetEquipmentTypeQuery,
  useLazyGetEquipmentTypeQuery,
  useLazyGetEquipmentByTypeQuery,
  useLazyGetFacilityByTypeQuery,
  useLazyGetInspectionsByTypeQuery,
  useUpdateEquipmentTypeMutation,
  useLazyGetCurrentInspectionQuery,
  useCompleteInspectionMutation,
  useLazyGetInspectionByFacilityQuery,
  useLazyGetCorrectiveActionsQuery,
  useGetCorrectiveActionQuery,
  useLazyGetCorrectiveActionQuery,
  useLazyGetCurrentFacilityInspectionsQuery,
  useUpdateFacilityInfoMutation,
  useRefuseInspectionsMutation,
  useChangeCorrectiveActionMutation,
  useLazyGetInspectionFacilityByIdQuery,
  useGetReportListQuery,
  useLazyGetReportListQuery,
  useCreateReportMutation,
  useCreateFuelReceiptReportMutation,
  useGetStationsListQuery,
  useCreateStationMutation,
  useGetTerminalsListQuery,
  useCreateTerminalMutation,
  useUpdateStationMutation,
  useUpdateTerminalMutation,
  useDeleteStationMutation,
  useDeleteTerminalMutation,
  useCreateEquipmentMutation,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useLazyGetUsersIndexQuery,
  useGetUsersByAgencyQuery,
  useLazyGetChosenUserQuery,
  useGetUsersRolesQuery,
  useLazyGetUsersRolesQuery,
  useCreateUserMutation,
  useDeleteUserMutation,
  useUpdateUserMutation,
  useUpdatePasswordMutation,
  useUnAssignEquipmentMutation,
  useUpdateEquipmentMutation,
  useEquipmentDecommissionMutation,
  useEquipmentActivateMutation,
  useEquipmentUnarchivedMutation,
  useLazyGetReportsQuery,
  useLazyGetReportHistoryQuery,
  useLazyGetMissedInspectionsQuery,
  useLazyGetReturnToServiceQuery,
  useUpdateMissedInspectionMutation,
  useGetServiceRequestsQuery,
  useLazyGetServiceRequestsQuery,
  useUpdateServiceRequestMutation,
  useLazyGetSheetsQuery,
  useRemoveLogSheetMutation,
  useGetAgencyQuery,
  useLazyGetAgencyQuery,
  useLazyGetAgencyIndexQuery,
  useLazyGetAgencySubscribedQuery,
  useLazyGetInvitedAgencyIndexQuery,
  useDeleteInvitedAgencyMutation,
  useResendInvitedAgencyMutation,
  useCreateAgencyMutation,
  useUpdateAgancyMutation,
  useLazyRefreshInspectionsQuery,
  useLazyGetCurrentInspectionAndCategoryQuery,
  useLazyGetCurrentEquipmentQuery,
  useCreateServiceMutation,
  useSaveEquipmentFormMutation,
  useGetAssignedQuery,
  useLazyGetAssignedQuery,
  useLazyGetInspectionsPaginationQuery,
  useDeleteInspectionMutation,
  useSaveProfilePhotoMutation,
  useUploadFileMutation,
  useGetTimezonesQuery,
  useForgotPasswordMutation,
  useSendCodeMutation,
  useChangePasswordMutation,
  useLazyGetOutOfServiceQuery,
  useGetFuelTypesQuery,
  useGetSecretKeyMutation,
  useGetCorporateSecretKeyMutation,
  useRemoveReportMutation,
  useCheckReportMutation,
  useReportPdfLinkMutation,
  useUpdatePaymentMutation,
  useUpdateCorporatePaymentMutation,
  usePaymentCheckoutMutation,
  usePaymentCorporateCheckoutMutation,
  usePaymentConfirmMutation,
  usePaymentCorporateConfirmMutation,
  useBuyPhonesMutation,
  useGetHistorySubscriptionQuery,
  useGetHistoryPaymentsQuery,
  useGetHistoryLeasingQuery,
  useGetHistoryOrdersQuery,
  useGetPricesQuery,
  useGetCorporatePricesQuery,
  useLazyGetPricesQuery,
  useGetFuelReceiptQuery,
  useLazyGetFuelReceiptQuery,
  useGetOrdersListQuery,
  useLazyGetOrdersListQuery,
  useGetOrderQuery,
  useLazyGetOrderQuery,
  useGetSettingsQuery,
  useLazyGetSettingsQuery,
  useUpdateSettingMutation,
  useUpdateBillingMutation,
  useUpdateOrderMutation,
  useCreateManualOrderMutation,
  useDeleteEquipmentMutation,
  usePermanentlyDeleteMutation,
  useUpdateUserProfileMutation,
  useUpdateUserProfilePasswordMutation,
  useClearInspectionsMutation,
  useUpdateAgancySettingsMutation,
  useUpdateAgancyProfileMutation,
  useUpdateAgancyProfileSettingsMutation,
  useUpdateAgencyCorrectiveSettingsMutation,
  useGetUsersByAgencyIdQuery,
  useLazyGetUsersByAgencyIdQuery,
  useCancelSubscriptionMutation,
  useGetAgencyByIdQuery,
  useLazyGetTrialAgencyQuery,
  useLazyGetAgencyByIdQuery,
  useLazyGetOrdersListFboQuery,
  useLazyGetSubscriptionsFboQuery,
  useUpdateOrderShippingAddressMutation,
  useClearInspectionsFboMutation,
  useReactivateFboEquipmentsMutation,
  useGetCustomerRequestsQuery,
  useLazyGetCustomerRequestsQuery,
  useGetFieldOptionsQuery,
  useGetSignatureBasesQuery,
  useUpdateCustomerRequestMutation,
  useSentCustomerLinkMutation,
  useInviteFboMutation,
  useStartAgencyTrialMutation,
  useExtendAgencyTrialMutation,
  useLazyDashboardPersonalStatsQuery,
  useLazyDashboardEquipmentStatsQuery,
  useLazyDashboardPersonalInfoQuery,
  useLazyDashboardOutStandingInspectionsQuery,
  useLazyDashboardOutStandingRtsQuery,
  useLazyDashboardCorrectiveActionsQuery,
  useLazyDashboardOpenStatsQuery,
  useLazyDashboardAveragePerInspectionQuery,
  useLazyDashboardInspectionsQuery,
  useLazyGetFacilityByAgencyQuery,
  useCanAddFacilityMutation,
  useUpdateFboPricesMutation,
  useLazyMetricsInspectionsPlannedQuery,
  useLazyMetricsInspectionsYesterdayQuery,
  useLazyMetricsInspectionsMissedQuery,
  useLazyMetricsEquipmentCountTypeQuery,
  useLazyMetricsEquipmentRemovedYesterdayQuery,
  useLazyMetricsUpcomingInspectionsQuery,
  useLazyMetricsCorrectiveActionsStatsQuery,
  useLazyMetricsInspectionByEquipmentTypeQuery,
  useLazyMetricsAverageInspectionDurationQuery,
  useLazyMetricsEquipmentDecommissionsQuery,
  useLazyMetricsEquipmentRemovedQuery,
  useCreatePeriodInspectionMutation,
  useUnAssignFacilityMutation,
  useAssignFacilityMutation,
  useGetAgenciesWithEquipmentsQuery,
  useLazyGetAgenciesWithEquipmentsQuery,
  useCreateFiltersChangeMutation,
  useCreateFuelQualityRecordMutation,
  useCreateFuelReceiptInspectionMutation,
  useCreateDemandPeriodMutation,
  useLazyCorporateListQuery,
  useCreateCorporateAccountMutation,
  useAssignFboWithCorporateMutation,
  useUnAssignFboMutation,
  useLazyRegionsListQuery,
  useRegionsListQuery,
  useCreateRegionMutation,
  useDeleteRegionMutation,
  useLazyRegionDetailsQuery,
  useLazyDashboardCorporateFBOQuery,
  useLazyDashboardCorporateEquipmentQuery,
  useLazyDashboardCorporatePersonnelQuery,
  useUpdateCorporateProfileMutation,
  useUpdateCorporateCorrectiveMutation,
  useLazyCorporateDetailsQuery,
  useUpdateCorporateMutation,
  useCorporateUsersListQuery,
  useAgencyWaterTestKitTypesQuery,
  useUpdateCorporatePricingMutation,
  useUpdateCorporateBillingMutation,
  useUpdateSettingsAsCorporateMutation,
  useDeleteAgencyMutation,
  useSkipInspectionMutation,
  useLazyGetToolsQuery,
  useLazyGetCalibrationsQuery,
  useLazyGetCommentsQuery,
  useAddCommentsMutation,
  useRemoveFacilityMutation,
  useAgencyInspectionEnableMutation,
  useAgencyDeactivateMutation,
  useAgencyReactivateMutation,
  useAgencySetSubscriptionMutation,
  useLazyAgencyInactiveQuery,
  useCorporateUpdateStatusMutation,
  useSetAgencyToUserMutation,
  useLazyDemandInspectionsQuery,
  useCreateHoseEquipmentMutation,
  useUpdateHoseEquipmentMutation,
  useDeleteHoseEquipmentMutation,
  useLazyGetCustomCategoriesQuery,
  useLazyGetCustomFormsInspectionsQuery,
  useCreateCustomCategoryMutation,
  useLazyGetCustomFormDetailsQuery,
  useUpdateCustomCategoryMutation,
  useCreateEquipmentTypeMutation,
  useCreateCustomEquipmentTypeMutation,
  useCreateCustomInspectionMutation,
  useUpdateCustomInspectionMutation,
  useGetCustomInspectionsDetailsQuery,
  useLazyGetCustomInspectionsDetailsQuery,
  usePublishCustomInspectionMutation,
  useExtendCustomInspectionMutation,
  useUpdateOpenDaysMutation,
  useAssignCustomInspectionMutation,
  useLazyGetCustomFormDetailsAgenciesQuery,
  useLazyGetCustomFormDetailsAgenciesEquipmentsQuery,
  useDeleteCustomInspectionCategoryMutation,
  useDeleteCustomInspectionMutation,
  useToggleCustomFormMutation,
  useToggleCorporateCustomFormMutation,
  useRemoveFuelInspectionMutation,
  useSetManualDateMutation,
  useUpdateUserRoleMutation,
  useSetUserRegionMutation,
  useUpdateUserSsoIgnoreMutation,
  useLoginAsUserMutation,
  useLazyEquipmentsByFacilityQuery,
  useTransferEquipmentMutation,
  // TODO: Not needed refactoring
  useLazyGetCustomInspectionsPeriodsQuery,
} = inspectionApi;
